import pptxgen from "pptxgenjs";

const COLORS = {
  navy: "102A43",
  blue: "1F6FEB",
  cyan: "38BDF8",
  green: "22C55E",
  amber: "F59E0B",
  red: "EF4444",
  ink: "243B53",
  muted: "627D98",
  pale: "EAF2FF",
  line: "D9E2EC",
  white: "FFFFFF",
  background: "F7F9FC",
};

const CLASSIFICATION_COLORS = [
  "1F6FEB",
  "22C55E",
  "F59E0B",
  "8B5CF6",
  "EF4444",
  "14B8A6",
  "64748B",
  "EC4899",
];

function addHeader(slide, title, subtitle) {
  slide.background = { color: COLORS.background };
  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 0.16,
    h: 7.5,
    fill: { color: COLORS.blue },
    line: { color: COLORS.blue },
  });
  slide.addText(title, {
    x: 0.55,
    y: 0.3,
    w: 8.8,
    h: 0.42,
    fontFace: "Aptos Display",
    fontSize: 24,
    bold: true,
    color: COLORS.navy,
    margin: 0,
  });
  if (subtitle) {
    slide.addText(subtitle, {
      x: 9.5,
      y: 0.37,
      w: 3.25,
      h: 0.25,
      fontFace: "Aptos",
      fontSize: 9,
      color: COLORS.muted,
      align: "right",
      margin: 0,
    });
  }
  slide.addShape("line", {
    x: 0.55,
    y: 0.87,
    w: 12.2,
    h: 0,
    line: { color: COLORS.line, width: 1 },
  });
}

function addFooter(slide, data) {
  slide.addText(`${data.team} · ${data.iteration.name} · Sprint Review`, {
    x: 0.55,
    y: 7.16,
    w: 8,
    h: 0.18,
    fontFace: "Aptos",
    fontSize: 8,
    color: COLORS.muted,
    margin: 0,
  });
}

function addKpi(slide, { x, y, w, value, label, color = COLORS.blue }) {
  slide.addShape("roundRect", {
    x,
    y,
    w,
    h: 1.08,
    rectRadius: 0.08,
    fill: { color: COLORS.white },
    line: { color: COLORS.line, width: 1 },
    shadow: { type: "outer", color: "B8C4D1", opacity: 0.15, blur: 1, angle: 45, distance: 1 },
  });
  slide.addShape("rect", {
    x,
    y,
    w: 0.08,
    h: 1.08,
    fill: { color },
    line: { color },
  });
  slide.addText(String(value), {
    x: x + 0.25,
    y: y + 0.16,
    w: w - 0.4,
    h: 0.42,
    fontFace: "Aptos Display",
    fontSize: 25,
    bold: true,
    color: COLORS.navy,
    margin: 0,
  });
  slide.addText(label, {
    x: x + 0.25,
    y: y + 0.67,
    w: w - 0.4,
    h: 0.22,
    fontFace: "Aptos",
    fontSize: 9,
    color: COLORS.muted,
    margin: 0,
  });
}

function groupValue(items) {
  const groups = new Map();
  for (const item of items) {
    if (!groups.has(item.parent)) groups.set(item.parent, []);
    groups.get(item.parent).push(item);
  }
  return [...groups.entries()].map(([need, children]) => ({
    need,
    delivered: children.map((item) => item.summary).join(" · "),
    description: children.map((item) => item.benefit).join(" · "),
  }));
}

function analyzeMeetings(text) {
  if (!text?.trim()) {
    return [
      {
        topic: "Sin resúmenes de reuniones asociados",
        impact: "No fue posible identificar riesgos o bloqueos desde Fathom.",
        decision: "Validar manualmente con el equipo.",
      },
    ];
  }
  const riskWords = /riesg|bloque|depend|retras|imped|alert|afecta|pendiente|decisi|incidente|error/i;
  const candidates = text
    .split(/\n|(?<=[.!?])\s+/)
    .map((line) => line.replace(/^[-*•\d.)\s#]+/, "").trim())
    // Los encabezados y etiquetas del resumen de Fathom no son temas en sí.
    .filter((line) => line.length > 18 && !/^(compromisos|propósito de la reunión|puntos clave)\b/i.test(line))
    .filter((line, index, lines) => lines.indexOf(line) === index);
  const ordered = [...candidates].sort(
    (a, b) => Number(riskWords.test(b)) - Number(riskWords.test(a)),
  );
  return ordered.slice(0, 6).map((topic) => ({
    topic: topic.length > 240 ? `${topic.slice(0, 239).replace(/\s+\S*$/, "")}…` : topic,
    impact: riskWords.test(topic)
      ? "Puede afectar alcance, fecha o calidad de la entrega."
      : "Requiere seguimiento para sostener el avance del sprint.",
    decision: /decisi|aproba|defin|valid/i.test(topic)
      ? "Definir responsable y decisión con stakeholders."
      : "Confirmar responsable, fecha y siguiente acción.",
  }));
}

function tableOptions(widths) {
  return {
    x: 0.55,
    y: 1.15,
    w: 12.2,
    h: 5.65,
    colW: widths,
    border: { type: "solid", color: COLORS.line, pt: 1 },
    fill: COLORS.white,
    color: COLORS.ink,
    fontFace: "Aptos",
    fontSize: 11,
    margin: 0.1,
    valign: "mid",
    breakLine: false,
    autoFit: false,
    rowH: 1.08,
  };
}

export async function createPresentation(data, { sprintGoal, meetingNotes } = {}) {
  const pptx = new pptxgen();
  pptx.layout = "LAYOUT_WIDE";
  pptx.author = "Generador Sprint Review";
  pptx.subject = `Sprint Review ${data.iteration.name}`;
  pptx.title = `${data.team} · ${data.iteration.name}`;
  pptx.company = "Satrack";
  pptx.lang = "es-CO";
  pptx.theme = {
    headFontFace: "Aptos Display",
    bodyFontFace: "Aptos",
    lang: "es-CO",
  };

  let slide = pptx.addSlide();
  slide.background = { color: COLORS.navy };
  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 4.35,
    h: 7.5,
    fill: { color: COLORS.blue, transparency: 5 },
    line: { color: COLORS.blue },
  });
  slide.addShape("arc", {
    x: 9.6,
    y: -1.6,
    w: 5.2,
    h: 5.2,
    adjustPoint: 0.25,
    rotate: 25,
    fill: { color: COLORS.cyan, transparency: 75 },
    line: { color: COLORS.cyan, transparency: 100 },
  });
  slide.addText("SPRINT REVIEW", {
    x: 0.75,
    y: 1.55,
    w: 3.25,
    h: 0.35,
    fontFace: "Aptos",
    fontSize: 15,
    bold: true,
    color: COLORS.white,
    charSpacing: 3,
    margin: 0,
  });
  slide.addText(data.iteration.name, {
    x: 0.75,
    y: 2.04,
    w: 8.9,
    h: 0.82,
    fontFace: "Aptos Display",
    fontSize: 42,
    bold: true,
    color: COLORS.white,
    margin: 0,
    breakLine: false,
  });
  slide.addText(data.team, {
    x: 0.75,
    y: 3.02,
    w: 5,
    h: 0.45,
    fontFace: "Aptos Display",
    fontSize: 21,
    color: "DCEBFF",
    margin: 0,
  });
  const dateRange = [data.iteration.startDate, data.iteration.finishDate]
    .filter(Boolean)
    .map((date) => new Intl.DateTimeFormat("es-CO", { dateStyle: "medium" }).format(new Date(date)))
    .join(" — ");
  slide.addText(dateRange, {
    x: 0.75,
    y: 6.55,
    w: 5.5,
    h: 0.25,
    fontFace: "Aptos",
    fontSize: 11,
    color: "DCEBFF",
    margin: 0,
  });

  slide = pptx.addSlide();
  addHeader(slide, "Resumen de la iteración", `${data.team} · ${data.iteration.name}`);
  slide.addText("SPRINT GOAL", {
    x: 0.7,
    y: 1.25,
    w: 2.2,
    h: 0.24,
    fontSize: 11,
    bold: true,
    color: COLORS.blue,
    charSpacing: 1.5,
    margin: 0,
  });
  slide.addText(sprintGoal || data.sprintGoal || "Sprint Goal no registrado", {
    x: 0.7,
    y: 1.65,
    w: 11.7,
    h: 1.25,
    fontFace: "Aptos Display",
    fontSize: 26,
    bold: true,
    color: COLORS.navy,
    valign: "mid",
    margin: 0.05,
    fit: "shrink",
  });
  addKpi(slide, { x: 0.7, y: 3.45, w: 3.3, value: data.indicators.totalItems, label: "HISTORIAS TRABAJADAS" });
  addKpi(slide, { x: 4.25, y: 3.45, w: 3.3, value: data.indicators.planned, label: "ESFUERZO PLANEADO", color: COLORS.amber });
  addKpi(slide, { x: 7.8, y: 3.45, w: 3.3, value: data.indicators.executed, label: "ESFUERZO EJECUTADO", color: COLORS.green });
  const delivered = data.items.filter((item) => item.completed).length;
  slide.addText(`${delivered} de ${data.indicators.totalItems} historias cerradas`, {
    x: 0.7,
    y: 5.25,
    w: 5.3,
    h: 0.35,
    fontSize: 17,
    bold: true,
    color: COLORS.ink,
    margin: 0,
  });
  slide.addShape("rect", {
    x: 0.7,
    y: 5.82,
    w: 10.4,
    h: 0.22,
    fill: { color: COLORS.line },
    line: { color: COLORS.line },
  });
  slide.addShape("rect", {
    x: 0.7,
    y: 5.82,
    w: data.indicators.totalItems ? 10.4 * (delivered / data.indicators.totalItems) : 0,
    h: 0.22,
    fill: { color: COLORS.green },
    line: { color: COLORS.green },
  });
  addFooter(slide, data);

  const valueRows = groupValue(data.items);
  const valueChunks = valueRows.length
    ? Array.from({ length: Math.ceil(valueRows.length / 4) }, (_, index) =>
        valueRows.slice(index * 4, index * 4 + 4),
      )
    : [[{ need: "Sin información", delivered: "No hay HUs incluidas.", description: "Revise el sprint y los filtros." }]];
  valueChunks.forEach((chunk, index) => {
    const valueSlide = pptx.addSlide();
    addHeader(
      valueSlide,
      index ? "Valor entregado · continuación" : "Valor entregado",
      `${data.team} · ${data.iteration.name}`,
    );
    const rows = [
      [
        { text: "Necesidad", options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
        { text: "Solución entregada / avances", options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
        { text: "Descripción / beneficio", options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
      ],
      ...chunk.map((row) => [row.need, row.delivered, row.description]),
    ];
    valueSlide.addTable(rows, tableOptions([2.8, 4.3, 5.1]));
    addFooter(valueSlide, data);
  });

  slide = pptx.addSlide();
  addHeader(slide, "Indicadores del sprint", `${data.team} · ${data.iteration.name}`);
  addKpi(slide, { x: 0.7, y: 1.2, w: 2.4, value: data.indicators.totalItems, label: "HUs TRABAJADAS" });
  addKpi(slide, { x: 3.3, y: 1.2, w: 2.4, value: data.indicators.planned, label: "PLANEADO", color: COLORS.amber });
  addKpi(slide, { x: 5.9, y: 1.2, w: 2.4, value: data.indicators.executed, label: "EJECUTADO", color: COLORS.green });
  slide.addChart(
    pptx.ChartType.bar,
    [
      {
        name: "Esfuerzo",
        labels: ["Planeado", "Ejecutado"],
        values: [data.indicators.planned, data.indicators.executed],
      },
    ],
    {
      x: 0.7,
      y: 2.65,
      w: 5.1,
      h: 3.7,
      catAxisLabelFontSize: 11,
      valAxisLabelFontSize: 9,
      showLegend: false,
      showTitle: true,
      title: "Planeado vs. ejecutado",
      showValue: true,
      chartColors: [COLORS.blue],
      showCatName: false,
      showValAxisTitle: false,
      showCatAxisTitle: false,
      valGridLine: { color: COLORS.line, width: 1 },
      border: { color: COLORS.line, width: 1 },
    },
  );
  const classes = Object.entries(data.indicators.classifications);
  slide.addChart(
    pptx.ChartType.doughnut,
    [
      {
        name: "Clasificación",
        labels: classes.length ? classes.map(([name]) => name) : ["Sin esfuerzo registrado"],
        values: classes.length ? classes.map(([, value]) => value) : [1],
      },
    ],
    {
      x: 6.15,
      y: 1.2,
      w: 6.25,
      h: 5.15,
      holeSize: 55,
      showLegend: true,
      legendPos: "r",
      legendFontSize: 9,
      showPercent: true,
      showTitle: true,
      title: "Distribución del esfuerzo por clasificación",
      chartColors: CLASSIFICATION_COLORS,
      border: { color: COLORS.line, width: 1 },
    },
  );
  addFooter(slide, data);

  slide = pptx.addSlide();
  addHeader(slide, "Velocidad histórica del equipo", `${data.team} · últimos sprints disponibles`);
  slide.addChart(
    pptx.ChartType.line,
    [
      {
        name: "Velocidad",
        labels: data.history.map((item) => item.name),
        values: data.history.map((item) => item.velocity),
      },
    ],
    {
      x: 0.75,
      y: 1.35,
      w: 11.85,
      h: 5.35,
      showLegend: false,
      showTitle: false,
      showValue: true,
      lineSize: 3,
      catAxisLabelRotate: -25,
      catAxisLabelFontSize: 10,
      valAxisLabelFontSize: 9,
      valAxisMinVal: 0,
      valGridLine: { color: COLORS.line, width: 1 },
      chartColors: [COLORS.blue],
      showMarker: true,
      markerSize: 7,
      border: { color: COLORS.line, width: 1 },
    },
  );
  addFooter(slide, data);

  const alerts = analyzeMeetings(meetingNotes);
  slide = pptx.addSlide();
  addHeader(slide, "Análisis y alertas", `${data.team} · ${data.iteration.name}`);
  slide.addTable(
    [
      [
        { text: "Análisis y alertas", options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
        { text: "Impacto", options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
        { text: "Decisión requerida", options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
      ],
      ...alerts.map((row) => [row.topic, row.impact, row.decision]),
    ],
    { ...tableOptions([4.5, 3.7, 4]), rowH: 0.78, fontSize: 10 },
  );
  addFooter(slide, data);

  const outputType = typeof window === "undefined" ? "nodebuffer" : "blob";
  return pptx.write({ outputType });
}
